import { SingleValue } from "react-select"
import CustomSelect, {
  SelectOption,
} from "../../component/customSelect/customSelect"
import CustomInput from "../../component/CustomInput/CustomInput"
import { complexities } from "../../utils/complexity"
import { GRADES } from "../../utils/grade"
import {
  PreviewSubjectOption,
  ProcessedQuestionContent,
  PreviewFieldErrors,
} from "../QuestionPreview/types"
import { normalizeYearInput } from "../../utils/yearValidation"
import LevelSelectors, {
  LevelFieldErrors,
} from "../Questions/components/LevelSelectors"

interface QuestionEditFormV2Props {
  content: ProcessedQuestionContent
  subjects: PreviewSubjectOption[]
  onChange: (name: string, value: unknown) => void
  fieldErrors?: PreviewFieldErrors
}

const QuestionEditFormV2 = ({
  content,
  subjects,
  onChange,
  fieldErrors = {},
}: QuestionEditFormV2Props) => {
  const selectedSubject = subjects.find(
    (item) => item.value === content.subject
  )

  const levelErrors: LevelFieldErrors = {
    level_1: fieldErrors.topic,
    level_2: fieldErrors.level_2,
    level_3: fieldErrors.level_3,
    level_4: fieldErrors.level_4,
  }

  return (
    <aside className='question_preview_v2__meta'>
      <div className='select subject_select'>
        <label>
          Subject <span>*</span>
        </label>
        <CustomSelect<PreviewSubjectOption>
          placeholder='Select subject'
          data={subjects}
          onChange={(value: SingleValue<PreviewSubjectOption>) => {
            onChange("subject", value)
          }}
          value={selectedSubject ?? null}
          error={fieldErrors.subject}
        />
      </div>

      {content.subject && (
        <LevelSelectors
          subjectId={content.subject}
          levelLabels={selectedSubject?.level_labels}
          values={{
            level_1: content.topic_id ?? "",
            level_2: content.level_2_id ?? "",
            level_3: content.level_3_id ?? "",
            level_4: content.level_4_id ?? "",
          }}
          names={{
            level_1: content.topic ?? null,
            level_2: content.level_2_name ?? null,
            level_3: content.level_3_name ?? null,
            level_4: content.level_4_name ?? null,
          }}
          onChange={(values, names) => onChange("levels", { values, names })}
          errors={levelErrors}
          className='select'
        />
      )}

      <div className='field'>
        <label htmlFor={`subParts-${content.subject}`}>Sub-parts</label>
        <textarea
          id={`subParts-${content.subject}`}
          className='question_preview_v2__textarea'
          placeholder='e.g. a, b, c'
          value={content.subPartsText}
          onChange={(event) => onChange("subPartsText", event.target.value)}
        />
      </div>

      <div className='select grade_select'>
        <label>
          Level <span>*</span>
        </label>
        <CustomSelect
          placeholder='Select level'
          data={GRADES}
          onChange={(value: SingleValue<SelectOption>) => {
            onChange("grade", value)
          }}
          value={GRADES.find((item) => item.value === content.grade) ?? null}
          error={fieldErrors.grade}
        />
      </div>

      <div className='select complexity_select'>
        <label>
          Difficulty <span>*</span>
        </label>
        <CustomSelect
          placeholder='Select difficulty'
          data={complexities}
          onChange={(value: SingleValue<SelectOption>) => {
            onChange("complexity", value)
          }}
          value={
            complexities.find((item) => item.value === content.complexity) ??
            null
          }
          error={fieldErrors.complexity}
        />
      </div>

      <div className='field'>
        <label>Source</label>
        <CustomInput
          name='source'
          placeholder='Past paper, textbook, original'
          value={content.source}
          onChange={(event) => onChange("source", event.target.value)}
        />
      </div>

      <div className='field'>
        <label>Year</label>
        <CustomInput
          name='year'
          placeholder='e.g. 2024'
          value={content.year}
          onChange={(event) =>
            onChange("year", normalizeYearInput(event.target.value))
          }
          error={fieldErrors.year}
        />
      </div>

      <div className='field'>
        <label>Marks</label>
        <CustomInput
          name='marks'
          placeholder='Total marks'
          value={content.marks}
          onChange={(event) => onChange("marks", event.target.value)}
        />
      </div>
    </aside>
  )
}

export default QuestionEditFormV2
